/**
 * useVariationChainHandlers — wraps useVariationChains with UI handler callbacks.
 * Extracted from App.tsx to reduce god component complexity.
 */

import { useCallback } from 'react';
import { useToast } from '@components/ui';
import { useVariationChains } from '@hooks/useVariationChains';
import type { ParsedSunoOutput } from '@/types';
import type { SpawnStrategy, VariationNode } from '@/types/variationChain.types';

export function useVariationChainHandlers(
  output: ParsedSunoOutput | null,
  sunoCookie: string,
) {
  const {
    activeChain, createChain, deleteChain,
    spawnVariations, rateNode, triggerNodeGeneration,
    getTree, isLoading: isChainLoading,
  } = useVariationChains();

  const { addToast } = useToast();
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    addToast({ message, variant: type });
  }, [addToast]);

  const handleStartVariationChain = useCallback(async () => {
    if (!output) return;
    const chain = await createChain(
      output.title || 'Variation Chain',
      {
        title: output.title,
        prompt: output.lyricsWithTags,
        tags: output.style,
        negativeTags: output.excludedStyle,
      }
    );
    if (chain) {
      showToast(`Variation chain started for "${output.title}"`, 'success');
    } else {
      showToast('Failed to create variation chain', 'error');
    }
  }, [output, createChain, showToast]);

  const handleSpawnVariations = useCallback(async (parentNodeId: string, count: number, strategy: SpawnStrategy) => {
    if (!activeChain || !output) return;
    const spawnedNodes = await spawnVariations(
      activeChain.id,
      parentNodeId, count, strategy,
      output.lyricsWithTags || '',
      output.style || '',
      output.excludedStyle || '',
      output.title || 'Variation'
    );
    for (const node of spawnedNodes) {
      if (sunoCookie && node.status === 'pending') {
        const err = await triggerNodeGeneration(activeChain.id, node, sunoCookie);
        if (err) {
          showToast(`Generation failed: ${err}`, 'error');
          break;
        }
      }
    }
  }, [activeChain, output, spawnVariations, triggerNodeGeneration, sunoCookie, showToast]);

  const handleRateVariationNode = useCallback((nodeId: string, rating: number) => {
    if (!activeChain) return;
    rateNode(activeChain.id, nodeId, rating);
  }, [activeChain, rateNode]);

  const handleTriggerNodeGen = useCallback(async (node: VariationNode) => {
    if (!activeChain || !sunoCookie) {
      showToast('Suno cookie not set — configure in Settings', 'error');
      return;
    }
    const err = await triggerNodeGeneration(activeChain.id, node, sunoCookie);
    if (err) showToast(`Generation failed: ${err}`, 'error');
  }, [activeChain, sunoCookie, triggerNodeGeneration, showToast]);

  const handleDeleteChain = useCallback(() => {
    if (!activeChain) return;
    deleteChain(activeChain.id);
    showToast('Variation chain deleted', 'success');
  }, [activeChain, deleteChain, showToast]);

  return {
    activeChain, getTree, isChainLoading,
    handleStartVariationChain,
    handleSpawnVariations,
    handleRateVariationNode,
    handleTriggerNodeGen,
    handleDeleteChain,
  };
}
